// requestAnimationFrame() polyfill by Erik Möller, Paul Irish, and Tino Zijdel.
// https://gist.github.com/paulirish/1579671

import Swal from 'sweetalert2'
import { phrases } from '../i18n/schema'
import { processInlineFormatting } from '../distance/markdownInstructionParser'
;(function () {
  let lastTime = 0
  const vendors = ['ms', 'moz', 'webkit', 'o']

  for (let x = 0; x < vendors.length && !window.requestAnimationFrame; ++x) {
    window.requestAnimationFrame = window[`${vendors[x]}RequestAnimationFrame`]
    window.cancelAnimationFrame =
      window[`${vendors[x]}CancelAnimationFrame`] ||
      window[`${vendors[x]}CancelRequestAnimationFrame`]
  }

  if (!window.requestAnimationFrame)
    // eslint-disable-next-line no-unused-vars
    window.requestAnimationFrame = function (callback, element) {
      const currTime = performance.now()
      const timeToCall = Math.max(0, 16 - (currTime - lastTime))
      const id = window.setTimeout(function () {
        callback(currTime + timeToCall)
      }, timeToCall)
      lastTime = currTime + timeToCall
      return id
    }

  if (!window.cancelAnimationFrame)
    window.cancelAnimationFrame = function (id) {
      clearTimeout(id)
    }

  // Object.assign() polyfill by spiralx
  // https://gist.github.com/spiralx/68cf40d7010d829340cb
  if (!Object.assign) {
    Object.defineProperty(Object, 'assign', {
      enumerable: false,
      configurable: true,
      writable: true,
      value: function (target) {
        if (target === undefined || target === null) {
          throw new TypeError()
        }

        const to = Object(target)
        for (let i = 1; i < arguments.length; i++) {
          let nextSource = arguments[i]
          if (nextSource === undefined || nextSource === null) {
            continue
          }
          nextSource = Object(nextSource)

          const keysArray = Object.keys(Object(nextSource))
          for (
            let nextIndex = 0, len = keysArray.length;
            nextIndex < len;
            nextIndex++
          ) {
            const nextKey = keysArray[nextIndex]
            const desc = Object.getOwnPropertyDescriptor(nextSource, nextKey)
            if (desc?.enumerable) {
              to[nextKey] = nextSource[nextKey]
            }
          }
        }
        return to
      },
    })
  }
})()

export function safeExecuteFunc(f, ...a) {
  if (f && typeof f === 'function') {
    if (a.length) return f(...a)
    return f()
  }
}

export const emptyFunc = () => {}

// http://stackoverflow.com/questions/951021/what-is-the-javascript-version-of-sleep
export function sleep(time) {
  return new Promise(resolve => setTimeout(resolve, time))
}

/* -------------------------------------------------------------------------- */

// Enter fullscreen
export async function getFullscreen(L = 'en', RC = null) {
  if (isFullscreen()) {
    return true
  }
  if (
    document.fullscreenEnabled ||
    document.webkitFullscreenEnabled ||
    document.mozFullScreenEnabled ||
    document.msFullscreenEnabled
  ) {
    try {
      return await fullScreen()
    } catch (e) {
      console.log(e)
      // ask for user interaction
      let value = false
      if (RC) {
        RC.disableKeypadHandler = true
      }
      await Swal.fire({
        html: phrases.EE_FullScreenOk[L],
        confirmButtonText: 'OK',
        preConfirm: async () => {
          value = await fullScreen()
        },
      })
      if (RC) {
        RC.disableKeypadHandler = false
      }
      return value
    }
  }

  return false
}

const fullScreen = async () => {
  const element = document.documentElement
  if (element.requestFullscreen) {
    await element.requestFullscreen()
    return true
  }

  if (element.mozRequestFullScreen) {
    await element.mozRequestFullScreen()
    return true
  }

  if (element.webkitRequestFullscreen) {
    await element.webkitRequestFullscreen()
    return true
  }

  if (element.msRequestFullscreen) {
    await element.msRequestFullscreen()
    return true
  }
  return false
}

export function isFullscreen() {
  return (
    document.fullscreenElement != null ||
    document.webkitFullscreenElement != null ||
    document.mozFullScreenElement != null ||
    document.msFullscreenElement != null
  )
}

// Exit fullscreen
export async function exitFullscreen() {
  if (!isFullscreen()) {
    return true
  }

  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen()
    } else if (document.webkitExitFullscreen) {
      await document.webkitExitFullscreen()
    } else if (document.mozCancelFullScreen) {
      await document.mozCancelFullScreen()
    } else if (document.msExitFullscreen) {
      await document.msExitFullscreen()
    }
    return true
  } catch (e) {
    console.error('Error exiting fullscreen:', e)
    return false
  }
}

// Force fullscreen unconditionally (for page arrival)
// Uses getFullscreen with the language and RC instance
export async function forceFullscreen(L = 'en', RC = null) {
  return await getFullscreen(L, RC)
}

// Enforce fullscreen on SPACE key press
// If not in fullscreen:
//   1. Flush the key press (return false)
//   2. Force fullscreen
//   3. Pause 4 seconds
// Returns true if in fullscreen (key press should be processed)
// Returns false if was not in fullscreen (key press should be ignored)
export async function enforceFullscreenOnSpacePress(L = 'en', RC = null) {
  if (isFullscreen()) {
    // Already in fullscreen, allow the key press to proceed
    return true
  }

  // Not in fullscreen - force it and wait
  console.log('Not in fullscreen - forcing fullscreen and waiting 4 seconds')
  await getFullscreen(L, RC)
  await sleep(4000)

  // Return false to indicate the key press should be flushed (ignored)
  return false
}

/* -------------------------------------------------------------------------- */

/**
 * Show a centered "Loading video ..." message while trackDistance
 * brings the camera back, loads FaceMesh, and preloads instruction
 * media before the first interactive page (paper-size selection,
 * blindspot intro, credit-card test, etc.) renders. Idempotent: a
 * second call while the message is already on screen is a no-op so
 * the message stays put across multiple call sites instead of
 * flickering.
 *
 * The message is appended to <body> (not the calibration background)
 * so `_replaceBackground` calls don't tear it down. Hide explicitly
 * with `hideLoadingVideoMessage()` right before the next page is
 * shown.
 *
 * @param {object} RC - RemoteCalibrator instance (used for language).
 */
export function showLoadingVideoMessage(RC) {
  if (document.getElementById('rc-loading-video-message')) return

  const msg = document.createElement('div')
  msg.id = 'rc-loading-video-message'
  // z-index is below the SweetAlert2 container (.swal2-container,
  // 999999999) so camera permission / camera selection / fullscreen
  // popups render on top while the loading message is up. It still
  // sits above the calibration background (999999990).
  msg.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 999999992;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    color: #333;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    pointer-events: none;
    user-select: none;
  `

  // Spinner: light gray ring with a blue arc, rotating.
  const spinner = document.createElement('div')
  spinner.id = 'rc-loading-video-spinner'
  spinner.style.cssText = `
    width: 48px;
    height: 48px;
    box-sizing: border-box;
    border: 4px solid #e6e6e6;
    border-top-color: #1a73e8;
    border-radius: 50%;
  `
  msg.appendChild(spinner)
  try {
    spinner.animate(
      [{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }],
      { duration: 900, iterations: Infinity, easing: 'linear' },
    )
  } catch {
    // Web Animations API unavailable — the static ring is an acceptable fallback.
  }

  const label = document.createElement('div')
  label.id = 'rc-loading-video-label'
  // Prefer the plural phrase; fall back to the older singular phrase (which
  // may already be translated in the International Phrases doc), then to an
  // English default so the label is never blank if the phrase isn't loaded.
  label.innerHTML = phrases?.RC_LoadingVideos?.[RC?.L] || 'Loading videos …'
  label.style.cssText = `
    margin-top: 1.1rem;
    font-size: 1.4rem;
    color: #333;
  `
  msg.appendChild(label)

  // Progress bar: blue fill on a light gray track.
  const track = document.createElement('div')
  track.id = 'rc-loading-video-progress'
  track.style.cssText = `
    width: 360px;
    max-width: 70vw;
    height: 8px;
    margin-top: 1rem;
    background: #e6e6e6;
    border-radius: 4px;
    overflow: hidden;
  `
  const fill = document.createElement('div')
  fill.id = 'rc-loading-video-progress-fill'
  fill.style.cssText = `
    width: 0%;
    height: 100%;
    background: #1a73e8;
    border-radius: 4px;
    transition: width 0.2s ease;
  `
  track.appendChild(fill)
  msg.appendChild(track)

  const percent = document.createElement('div')
  percent.id = 'rc-loading-video-percent'
  percent.textContent = '0%'
  percent.style.cssText = `
    margin-top: 0.6rem;
    font-size: 1rem;
    color: #666;
  `
  msg.appendChild(percent)

  document.body.appendChild(msg)
}

/**
 * Update the loading popup's progress bar and percentage. Safe to call
 * before the popup exists (no-op) and idempotent.
 *
 * @param {number} loaded - Number of assets finished loading.
 * @param {number} total  - Total number of assets to load.
 */
export function setLoadingVideoProgress(loaded, total) {
  const msg = document.getElementById('rc-loading-video-message')
  if (!msg) return

  const fill = msg.querySelector('#rc-loading-video-progress-fill')
  const percent = msg.querySelector('#rc-loading-video-percent')
  if (!fill) return

  const safeTotal = Number.isFinite(total) && total > 0 ? total : 0
  const safeLoaded = Math.max(0, Math.min(loaded || 0, safeTotal))
  const pct = safeTotal ? Math.round((safeLoaded / safeTotal) * 100) : 0

  fill.style.width = `${pct}%`
  if (percent) percent.textContent = `${pct}%`
}

export function hideLoadingVideoMessage() {
  const msg = document.getElementById('rc-loading-video-message')
  if (msg) msg.remove()
}

/* -------------------------------------------------------------------------- */

export function constructInstructions(
  headline,
  description = null,
  scrollable = false,
  descriptionClass = '',
  position = null,
  headline2 = null,
  useDescriptionClassOnly = false,
  headlineId = 'instruction-title',
) {
  // Enable inline markdown (**bold**, *italic*, `code`, etc.) in instruction
  // copy. processInlineFormatting is idempotent, preserves newlines/HTML, and
  // is a no-op for plain text, so existing instructions render unchanged.
  const fmtHeadline = processInlineFormatting(headline)
  const fmtHeadline2 = headline2
    ? processInlineFormatting(headline2)
    : headline2
  const fmtDescription = description
    ? processInlineFormatting(description)
    : description
  return `<div class="calibration-instruction${
    scrollable ? ' calibration-instruction-scrollable' : ''
  }${position === 'left' ? ' calibration-instruction-left' : ''}"><h1 id="${headlineId}" style="white-space: pre-line; text-align: start; margin: 0 0 3rem 0;">${fmtHeadline}</h1>${fmtHeadline2 ? '<h1 style="white-space: pre-line; text-align: start; margin: 0 0 4rem 0;">' + fmtHeadline2 + '</h1>' : ''}${
    fmtDescription
      ? `<p id="instruction-body" class="${useDescriptionClassOnly ? descriptionClass : 'calibration-description ' + descriptionClass}">${fmtDescription}</p></div>`
      : ''
  }`
}

/* ----------------------------- Tiny functions ----------------------------- */

export function constrain(a, b0, b1) {
  return a < b0 ? b0 : a > b1 ? b1 : a
}

export function remap(v, a1, b1, a2, b2) {
  return a2 + (b2 - a2) * (((v - a1) * 1.0) / (b1 - a1))
}

export function dist2d(aX, aY, bX, bY) {
  return Math.sqrt((aX - bX) ** 2 + (aY - bY) ** 2)
}

// https://stackoverflow.com/a/30924333
export function powerOf2(v) {
  return v && !(v & (v - 1))
}

// https://stackoverflow.com/a/12646864
export function shuffle(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[array[i], array[j]] = [array[j], array[i]]
  }
  return array
}

export function toFixedNumber(n, digits) {
  const pow = 10 ** digits
  return Math.round(n * pow) / pow
}

// https://github.com/30-seconds/30-seconds-of-code/blob/master/snippets/median.md
export const median = arr => {
  const mid = Math.floor(arr.length / 2)
  const num = [...arr].sort((a, b) => a - b)
  return arr.length % 2 !== 0 ? num[mid] : (num[mid - 1] + num[mid]) / 2
}

// https://stackoverflow.com/a/41452260
export const average = array => array.reduce((a, b) => a + b) / array.length

// https://stackoverflow.com/a/49434653
export function randn_bm(min, max, skew = 1) {
  let u = 0
  let v = 0
  while (u === 0) u = Math.random()
  while (v === 0) v = Math.random()
  let num = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v)

  num = num / 10.0 + 0.5
  if (num > 1 || num < 0) num = randn_bm(min, max, skew)
  else {
    num = num ** skew
    num *= max - min
    num += min
  }
  return num
}

export const _copy = obj => {
  return JSON.parse(JSON.stringify(obj))
}

/**
 *
 * BLUR ALL
 *
 */
export function blurAll() {
  if ('activeElement' in document) document.activeElement.blur()
}

/* -------------------------------------------------------------------------- */

export const getClickOrTouchLocation = e => {
  if (e.type === 'touchstart' || e.type === 'touchmove') {
    const touch = e.touches[0]
    return { x: touch.clientX, y: touch.clientY }
  }
  return { x: e.clientX, y: e.clientY }
}

export function replaceNewlinesWithBreaks(str) {
  return str.replace(/\n/g, '<br/>')
}

/**
 * Scale a fixed-position container so all content fits within the viewport.
 * Resets any previous scaling, measures natural content height via scrollHeight,
 * and applies transform: scale() when content overflows the viewport.
 *
 * Must be called after every content change (step navigation, page switch, resize).
 */
export function fitToViewport(container) {
  if (!container) return

  container.style.transform = 'none'
  container.style.height = '100vh'
  container.style.width = '100vw'

  const contentHeight = container.scrollHeight
  const vh = window.innerHeight

  if (contentHeight <= vh || contentHeight === 0) return

  const scale = vh / contentHeight
  container.style.height = `${contentHeight}px`
  container.style.width = `${100 / scale}vw`
  container.style.transformOrigin = 'top left'
  container.style.transform = `scale(${scale})`
}

/**
 * Scale a .calibration-instruction panel so its content fits within the viewport.
 * For absolutely-positioned instruction panels used in check phases (e.g. checkDistance).
 */
export function fitInstructionPanelToViewport() {
  const el = document.querySelector('.calibration-instruction')
  if (!el) return

  el.style.transform = 'none'
  const contentHeight = el.scrollHeight
  const vh = window.innerHeight

  if (contentHeight <= vh || contentHeight === 0) return

  const scale = vh / contentHeight
  el.style.transformOrigin = 'top left'
  el.style.transform = `scale(${scale})`
}

export const getCameraResolutionXY = RC => {
  try {
    const cameraResolution = RC.gazeTracker?.webgazer?.getCameraResolutionXY()
    const width = Math.max(cameraResolution.width, cameraResolution.height)
    const height = Math.min(cameraResolution.width, cameraResolution.height)
    return [width, height]
  } catch (error) {
    console.error('Error getting camera resolution:', error)
    return [0, 0]
  }
}

/**
 * Returns true when the participant selected a camera in the BOTTOM row
 * of the Choose Camera page (i.e. their built-in camera is at the bottom
 * centre of the screen). The flag is recorded by `showCameraSelectionPopup`
 * in [src/components/popup.js] when the user clicks a row and only ever
 * becomes true if `calibrateDistanceAcceptBottomCameraBool === true`,
 * because otherwise the bottom row is never rendered.
 *
 * Defaults to top-camera behaviour when the flag has not been set yet.
 */
export const isBottomCenterCamera = RC => RC?.selectedCameraRow === 'bottom'

/**
 * Returns the camera anchor point [x, y] in **screen** CSS pixels
 * (window.screen.*). Top-centre for top-camera setups, bottom-centre
 * for bottom-camera setups. This is the value that calibration code
 * stores as `cameraXYPx` in calibration JSON / footXYPx geometry.
 *
 * Pass `RC` so we can read `RC.selectedCameraRow`.
 */
export const getCameraXYPx = RC => {
  const x = window.screen.width / 2
  const y = isBottomCenterCamera(RC) ? window.screen.height : 0
  return [x, y]
}

/**
 * Returns the camera anchor point [x, y] in **viewport** CSS pixels
 * (window.innerWidth / window.innerHeight). Used when the caller is
 * positioning DOM elements relative to the current viewport rather than
 * the physical screen.
 */
export const getCameraXYPxViewport = RC => {
  const x = window.innerWidth / 2
  const y = isBottomCenterCamera(RC) ? window.innerHeight : 0
  return [x, y]
}
